<template>
  <main id="main">
    <Loading :isProcessing="isProcessing"></Loading>
    <div v-if="!isProcessing">
      <DiagCorpHeader
        v-if="!isRendering"
        :corpName="this.corpName"
        :corporateFeatureInfoLabels="this.corporateFeatureInfoLabels"
        :corporateSelectable="isAdmin"
        :manageCorpName="manageCorpName"
        :isAdmin="isAdmin"
        :isDiagAvailablePlan="isDiagAvailablePlan"
        @select-corporate="emitSelectCorpList"
      ></DiagCorpHeader>
    </div>

    <!-- 初期表示時の画面 -->
    <div class="l-contents" v-if="showCreate">
      <h1 class="l-pageTitle"><img src="@/assets/img/common/icon-nav8_blue.svg" alt="">人的資本管理</h1>
      <div class="l-contentsBox">
        <div class="human-capital-welcome">
          <div class="human-capital-welcome__img"><img src="@/assets/img/common/hc-icon01.svg" alt=""></div>
          <p class="human-capital-welcome__catch">ヨクスルの人的資本管理へようこそ</p>
          <p class="l-text">まずは人的資本に関する実績を入力して現状を把握しましょう。</p>
          <div class="btn-basic is-inlne is-primary is-wide mt-30"><a @click="createNewYear">人的資本に関する実績値を入力</a></div>
        </div>
      </div>
    </div>

    <!-- 設定保存の画面(管理ユーザー) -->
    <div class="l-contents" v-if="showSetting && (this.isAdmin || this.isAllPermission) && !isNormal">
      <h1 class="l-pageTitle"><img src="@/assets/img/common/icon-nav8_blue.svg" alt="">人的資本管理</h1>
      <div class="l-contentsBox">
        <div class="human-capital-welcome">
          <div class="human-capital-welcome__img"><img src="@/assets/img/common/hc-icon01.svg" alt=""></div>
          <p class="human-capital-welcome__catch">ヨクスルの人的資本管理へようこそ</p>
          <div class="human-capital-welcome__body">
            <p class="l-text u-tac is-lg"><strong>人的資本を利用するために必須の情報を入力してください。</strong></p>
            <p class="l-text u-tac">この項目は <a class="l-textlink" @click="goCorp">法人情報 > 企業の特徴</a> からも設定できます。</p>

            <!-- エラー表示 -->
            <div class="l-contentsSection error" 
              v-if="$v.corporateFeatureInfo.accountClosingMonth.$error
              || $v.corporateFeatureInfo.industoryItemId.$error 
              || $v.corporateFeatureInfo.industorySubitemId.$error">
              <div v-if="$v.corporateFeatureInfo.accountClosingMonth.$error" style="padding-bottom:5px;">
                <span style="color:red; font-size: 1.5rem;margin-left: 20px;">決算月　　　　　　　：</span>
                <ValidationErrorMessage style="margin-left:20px;" v-show="$v.corporateFeatureInfo.accountClosingMonth.$error && !$v.corporateFeatureInfo.accountClosingMonth.required" validation="required"/>
              </div>
              <div v-if="$v.corporateFeatureInfo.industoryItemId.$error" style="padding-bottom:5px;">
                <span style="color:red; font-size: 1.5rem;margin-left: 20px;">主たる業種（大項目）：</span>
                <ValidationErrorMessage style="margin-left:20px;" v-show="$v.corporateFeatureInfo.industoryItemId.$error && !$v.corporateFeatureInfo.industoryItemId.required" validation="required"/>
              </div>
              <div v-if="$v.corporateFeatureInfo.industorySubitemId.$error" style="padding-bottom:5px;">
                <span style="color:red; font-size: 1.5rem;margin-left: 20px;">主たる業種（中項目）：</span>
                <ValidationErrorMessage style="margin-left:20px;" v-show="$v.corporateFeatureInfo.industorySubitemId.$error && !$v.corporateFeatureInfo.industorySubitemId.required" validation="required"/>
              </div>
            </div>

            <table class="l-form-table u-mt30">
              <tbody>
                <tr>
                  <th><span>決算月</span></th>
                  <td>
                    <div class="l-select w-auto">
                      <SelectBox v-model="$v.corporateFeatureInfo.$model.accountClosingMonth"
                        :options="monthList"
                        :selectedValue="this.corporateFeatureInfo.accountClosingMonth"></SelectBox>
                    </div>
                  </td>
                </tr>
                <!-- 主たる業種 -->
                <tr>
                  <th><span>主たる業種</span></th>
                  <td>
                    <div class="l-select w-auto">
                      <SelectBox
                        v-model="$v.corporateFeatureInfo.$model.industoryItemId"
                        :options="industoryItemArray"
                        :selectedValue="this.corporateFeatureInfo.industoryItemId"
                        @input="IndustoryItemChange"></SelectBox>
                    </div>
                    <div class="l-select w-auto">
                      <SelectBox
                        v-model="$v.corporateFeatureInfo.$model.industorySubitemId"
                        :disabled="this.industorySubitemDisabled"
                        :options="industorySubitemArray"
                        :selectedValue="this.corporateFeatureInfo.industorySubitemId"></SelectBox>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="u-tac u-mt30"><div class="btn-basic is-inlne is-primary"><button @click="saveCorpSettings">保存</button></div></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 設定保存の画面(一般ユーザー) -->
    <div class="l-contents" v-if="showSetting && isNormal">
      <h1 class="l-pageTitle"><img src="@/assets/img/common/icon-nav8_blue.svg" alt="">人的資本管理</h1>
      <div class="l-contentsBox">
        <div class="human-capital-welcome">
          <div class="human-capital-welcome__img"><img src="@/assets/img/common/hc-icon01.svg" alt=""></div>
          <p class="human-capital-welcome__catch">ヨクスルの人的資本管理へようこそ</p>
          <div class="human-capital-welcome__body">
            <p class="l-text u-tac is-lg"><strong>人的資本を利用するために必須の情報を入力してください。</strong></p>
            <p class="l-text u-tac u-mt30">人的資本を利用するためには下記の項目の設定が必要です。</p>
            <ul class="human-capital-welcome__list">
              <li>決算月</li>
              <li>主たる業種</li>
            </ul>
            <p class="l-text u-tac">管理権限を持った社労士にご相談ください。</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 通常コンテンツ -->
    <div v-if="!showCreate && !showSetting" class="l-contents">
      <h1 class="l-pageTitle"><img src="@/assets/img/common/icon-nav8_blue.svg" alt="">人的資本管理</h1>

      <div class="l-contentsBox">

        <div class="alert-box" v-if="!this.hasCurrentYearAchievementData">
          <p>昨年度の実績を把握するために年度を追加しませんか？</p>
          <div class="btn-create-new_wh" @click="addYear"><a class="js-add-year-modal-show">年度を追加する</a></div>
        </div>

        <div class="l-contentsTitle is-noicon u-mb20">
          <h2 class="l-contentsTitle__text">{{this.dispYear}}年{{this.accountMonth}}月期の実績</h2>
        </div>
        <div class="u-mb20">
          <div class="btn-basic is-middle is-noarrow"><a @click="goSummary()"><img src="@/assets/img/common/icon-graph.svg" alt="" class="btn-icon">過去５年の推移を見る</a></div>
        </div>
        <div class="financial-data">
          <div class="financial-data-body">
            <p class="financial-data__title">
              <img src="@/assets/img/common/icon-financial.svg" alt="">
              財務情報
            </p>
            <p class="financial-data__text">売上高：<strong><Currency :value="achievements['achievement_sales']"/></strong>千円</p>
            <p class="financial-data__text">粗利：<strong><Currency :value="achievements['achievement_gross_profit']"/></strong>千円</p>
            <p class="financial-data__text">経常利益：<strong><Currency :value="achievements['achievement_ordinary_profit']"/></strong>千円</p>
          </div>
          <div class="financial-data-link">
            <a @click="goPages(14, 'input')" class="link-arrow is-edit-link">編集</a>
            <a @click="goPages(14, 'report')" class="link-arrow is-report-link">レポート</a>
          </div>
        </div>

        <div class="l-contentsSection">
          <p class="l-text u-tar u-mb10">（）は業界平均値</p>
          <table class="table-base human-capital-table">
            <colgroup>
              <col style="width: 100px;">
              <col style="width: 25%;">
              <col>
              <col style="width: 120px">
              <col style="width: 120px">
            </colgroup>
            <tbody>
              <tr>
                <th class="bg-blue">採用</th>
                <th>
                  <p class="human-capital-table__help">
                  採用女性労働者割合 
                  <span class="icon-help" data-tooltip='採用した労働者に占める女性労働者の割合/直近の事業年度の女性の採用者数（中途採用含む）÷直近の事業年度の採用者数（中途採用含む）×100（％）'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span><strong class="u-tlg"><Currency :value="achievements['achievement_female_percentage']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['femalePercentage']" :unit="unitPer"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(1, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>
                <td>
                  <a @click="goPages(1, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th class="bg-blue" rowspan="2">定着</th>
                <th>
                  <p class="human-capital-table__help">
                    平均継続勤務年数
                    <span class="icon-help" data-tooltip='直近の事業年度（期末時点）における平均継続勤務年数（年換算　小数点1桁）'>
                      <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span>男性 <strong class="u-tlg"><Currency :value="achievements['achievement_male_employment_year']" :unit="unitYear"/></strong></span>
                      <span>（<Currency :value="averages['employmentYearsAveMale']" :unit="unitYear"/>）</span>
                    </p>
                    <p>
                      <span>女性 <strong class="u-tlg"><Currency :value="achievements['achievement_female_employment_year']" :unit="unitYear"/></strong></span>
                      <span>（<Currency :value="averages['employmentYearsAveFemale']" :unit="unitYear"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(2, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>
                <td>
                  <a @click="goPages(2, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th>
                  <p class="human-capital-table__help">
                    離職率
                    <span class="icon-help" data-tooltip='離職者数÷1月1日現在の労働者数×100（％）'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span>男性 <strong class="u-tlg"><Currency :value="achievements['achievement_male_turnover']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['turnoverMale']" :unit="unitPer"/>）</span>
                    </p>
                    <p>
                      <span>女性 <strong class="u-tlg"><Currency :value="achievements['achievement_female_turnover']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['turnoverFemale']" :unit="unitPer"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(3, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>

                <td>
                  <a @click="goPages(3, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th class="bg-blue" rowspan="4">人員構成</th>
                <th>
                  <p class="human-capital-table__help">
                    女性管理職比率
                    <span class="icon-help" data-tooltip='女性の管理職数÷管理職数×100（％）'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span><strong class="u-tlg"><Currency :value="achievements['achievement_female_manager_ratio']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['femaleManagersRatio']" :unit="unitPer"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(4, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>
                <td>
                  <a @click="goPages(4, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th>
                  <p class="human-capital-table__help">
                    平均年齢
                    <span class="icon-help" data-tooltip='直近の事業年度の1月1日現在の満年齢の平均'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span>男性 <strong class="u-tlg"><Currency :value="achievements['achievement_male_age_average']" :unit="unitAge"/></strong></span>
                      <span>（<Currency :value="averages['ageAveMale']" :unit="unitAge"/>）</span>
                    </p>
                    <p>
                      <span>女性 <strong class="u-tlg"><Currency :value="achievements['achievement_female_age_average']" :unit="unitAge"/></strong></span>
                      <span>（<Currency :value="averages['ageAveFemale']" :unit="unitAge"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(5, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>

                <td>
                  <a @click="goPages(5, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th>
                  <p class="human-capital-table__help">
                    障がい者雇用率
                    <span class="icon-help" data-tooltip='それぞれの事業年度における「障害者雇用状況報告書」を元にした障害者雇用率'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span><strong class="u-tlg"><Currency :value="achievements['achievement_disabilities_rate']" :unit="unitPer"/></strong></span>
<!--                      <span>（<Currency :value="averages['disabilitiesRate']" :unit="unitPer"/>）</span>-->
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(6, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>

                <td>
                  <a @click="goPages(6, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th>
                  <p class="human-capital-table__help">
                    高年齢者雇用率
                    <span class="icon-help" data-tooltip='それぞれの事業年度における「高年齢者雇用状況報告書」を元にした高年齢者雇用率'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span><strong class="u-tlg"><Currency :value="achievements['achievement_elderly_rate']" :unit="unitPer"/></strong></span>
<!--                      <span>（<Currency :value="averages['elderlyRate']" :unit="unitPer"/>）</span>-->
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(7, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>

                <td>
                  <a @click="goPages(7, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th class="bg-blue">安全衛生</th>
                <th>
                  <p class="human-capital-table__help">
                    労災発生件数
                    <span class="icon-help" data-tooltip='休業3日以内＝療養のみ及び休業3日以内（障害・死亡を除く）の労災発生件数<br>休業4日以上＝休業4日以上及び障害・死亡に至った労災発生件数'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span><strong class="u-tlg"><Currency :value="achievements['achievement_accidents_count']" :unit="unitCount"/></strong></span>
<!--                      <span>（<Currency :value="averages['accidentsCount']" :unit="unitPer"/>）</span>-->
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(8, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>

                <td>
                  <a @click="goPages(8, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th class="bg-blue" rowspan="2">労働条件</th>
                <th>
                  <p class="human-capital-table__help">
                    年次有給休暇取得率
                    <span class="icon-help" data-tooltip='年次有給休暇の取得率＝直近年度における「労働者が取得した有給休暇（労働基準法第39条）の日数」÷「労働者に与えられた有給休暇の日数」×100'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span>正社員<strong class="u-tlg"><Currency :value="achievements['achievement_regular_annual_acquisition_rate']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['annualAcquisitionRateReguler']" :unit="unitPer"/>）</span>
                    </p>
                    <p>
                      <span>非正規社員<strong class="u-tlg"><Currency :value="achievements['achievement_non_regular_annual_acquisition_rate']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['annualAcquisitionRateNotReguler']" :unit="unitPer"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(9, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>
                <td>
                  <a @click="goPages(9, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th>
                  <p class="human-capital-table__help">
                    1月あたりの平均残業時間
                    <span class="icon-help" data-tooltip='直近の事業年度における「各月の対象労働者（全ての労働者）の（法定時間外労働＋法定休日労働）の総時間数の合計」÷「対象労働者数」<br>これにより難い場合は [「各月の対象労働者（全ての労働者）の総労働時間数の合計」―「各月の法定労働時間の合計＝(40×各月の日数÷7)×対象労働者数」]÷「対象労働者数」'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span><strong class="u-tlg"><Currency :value="achievements['achievement_working_hours_average']" :unit="unitHour"/></strong></span>
                      <span>（<Currency :value="averages['overtimeAve']" :unit="unitHour"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(10, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>
                <td>
                  <a @click="goPages(10, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th class="bg-blue" rowspan="2">育児介護休業</th>
                <th>
                  <p class="human-capital-table__help">
                    育児休業取得率
                    <span class="icon-help" data-tooltip='男性の取得率＝男性労働者のうち育児休業をした数÷男性労働者のうち配偶者が出産した数×100（％）<br>女性の取得率＝女性労働者のうち育児休業をした数÷女性労働者のうち出産した数×100（％）'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span>男性 <strong class="u-tlg"><Currency :value="achievements['achievement_male_childcare_rate']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['childcareRateMale']" :unit="unitPer"/>）</span>
                    </p>
                    <p>
                      <span>女性 <strong class="u-tlg"><Currency :value="achievements['achievement_female_childcare_rate']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['childcareRateFemale']" :unit="unitPer"/>）</span>
                    </p>
                    <p>
                      <span>全体 <strong class="u-tlg"><Currency :value="achievements['achievement_all_childcare_rate']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['childcareRateCorp']" :unit="unitPer"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(11, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>

                <td>
                  <a @click="goPages(11, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th>
                  <p class="human-capital-table__help">
                    介護休業・短時間勤務取得者数
                    <span class="icon-help" data-tooltip='それぞれの事業年度における介護休業の取得者数と介護短時間勤務取得者数'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span><strong class="u-tlg"><Currency :value="achievements['achievement_nursing_care_leave']" :unit="unitPerson"/></strong></span>
<!--                      <span>（<Currency :value="averages['nursingCareLeave']" :unit="unitPerson"/>）</span>-->
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(12, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>

                <td>
                  <a @click="goPages(12, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
              <tr>
                <th class="bg-blue">賃金</th>
                <th>
                  <p class="human-capital-table__help">
                    年間平均賃金
                    <span class="icon-help" data-tooltip='賃金差異＝女性の年間平均賃金÷男性の年間平均賃金×100（％）'>
                    <img src="@/assets/img/common/icon-help.svg" alt="" width="16px"></span>
                  </p>
                </th>
                <td>
                  <div class="human-capital-table__number">
                    <p>
                      <span>男性 <strong class="u-tlg"><Currency :value="achievements['achievement_wage_ave_male']" :unit="unitCurrency"/></strong></span>
                      <span>（<Currency :value="averages['wageAveMale']" :unit="unitCurrency"/>）</span>
                    </p>
                    <p>
                      <span>女性 <strong class="u-tlg"><Currency :value="achievements['achievement_wage_ave_female']" :unit="unitCurrency"/></strong></span>
                      <span>（<Currency :value="averages['wageAveFemale']" :unit="unitCurrency"/>）</span>
                    </p>
                    <p>
                      <span>賃金差異 <strong class="u-tlg"><Currency :value="achievements['achievement_wage_ave_diff']" :unit="unitPer"/></strong></span>
                      <span>（<Currency :value="averages['wageAveDiff']" :unit="unitPer"/>）</span>
                    </p>
                  </div>
                </td>
                <td>
                  <a @click="goPages(13, 'input')" class="link-arrow is-edit-link">編集</a>
                </td>

                <td>
                  <a @click="goPages(13, 'report')" class="link-arrow is-report-link">レポート</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>

    </div><!-- l-contents end -->

    <div class="default-modal js-add-year-modal"  v-show="this.addYearAlert">
      <div class="default-modal__bg js-default-modal-close"></div>
      <div class="default-modal__content is-mini">
        <div class="default-modal__header">
          <a class="default-modal__close js-default-modal-close" href="#" @click="closeAddYearAlert"></a>
        </div>
        <div class="default-modal__body">
          <div class="l-headding is-blueline">新しい年度（{{this.currentYear}}年{{this.accountMonth}}月期）を作成します。</div>
          <ul class="l-list">
            <li>この操作を行うと最も古い年度「{{this.currentYear-4}}年{{this.accountMonth}}月期の実績」が非表示となります。</li>
            <li>実績が入力されていない新しい年度が追加されるため、カルテに表示されている人的資本に関する情報が全て空白になります。</li>
            <li>この操作はやり直しができません。操作を行う場合は下記のボタンをクリックしてください。</li>
          </ul>

          <div class="default-modal__footer" style="text-align: center;">
            <div class="btn-group">
              <div class="btn-basic is-inlne js-cancel is-middle">
                <button class=" js-default-modal-close" @click="closeAddYearAlert">キャンセル</button>
              </div>
              <div class="btn-basic is-inlne is-primary is-middle"><button class=" js-default-modal-close" @click="createNewYear">新しい年度を作成する</button></div>
            </div>
          </div>                
        </div>
      </div>
    </div>

  </main>
</template>

<script src="https://code.jquery.com/jquery-3.5.1.min.js "></script>

<script>
import Loading from '@/components/parts/common/Loading.vue';
import DiagCorpHeader from '@/components/parts/diag/DiagCorpHeader.vue';
import constant from '@/const';
import Currency from '@/components/parts/human_capital/parts/Currency.vue';
import SelectBox from '@/components/parts/form/SelectBox.vue';
import { required } from 'vuelidate/lib/validators';
import ValidationErrorMessage from '@/components/parts/form/ValidationErrorMessage.vue';

export default {
  components: {
    Loading,
    DiagCorpHeader,
    Currency,
    SelectBox,
    ValidationErrorMessage,
  },
  data() {
    return {
      isParamAccess: false,
      isProcessing: false,
      isRendering: false,
      latestDiagResult: {
        diagResultId: null,
        diagCourseId: null,
        title: '',
        courseName: '',
        hasRiskDiagResultItems: [],
        existExpiredUpCloseDiagResult: false,
        isOptionDiag: false,
        diagResultLastUpdateDate: null,
        hasVerifiedItem: false,
        diagResultMonthOverMonth: '',
      },
      employeeNumberTypeList: constant.EMPLOYEE_NUMBER_TYPE,
      employeeNumberType: "",
      execChangeDiagTitle: false,
      isDiagAvailablePlan: false,
      transitionList: constant.TRANSITION_LIST,

      isSystemAdmin: false,
      isAllPermission: false,
      isAdmin: false,
      isNormal: false,

      hasCurrentYearAchievementData: false,
      achievements: {},
      averages: [],
      accountMonth: '',
      currentYear: null,
      dispYear: null,

      unitPer: '％',
      unitYear: '年',
      unitAge: '歳',
      unitCount: '件',
      unitHour: '時間',
      unitPerson: '名',
      unitCurrency: '千円',

      addYearAlert:false,
      showSetting: false,
      showCreate : false,

      corporateFeatureInfo: {},
      monthList: constant.MONTH_LIST,
      industoryItemArray: constant.INDUSTORY_ITEM,
      industorySubitemArray: [],
      industorySubitemDisabled: true,
    }
  },
  validations: {
    corporateFeatureInfo: {
      accountClosingMonth: {required},
      industoryItemId: {required},              // 業種大項目
      industorySubitemId: {required},           // 業種中項目
    }
  },
  computed: {
    corpId() {
      return this.$store.getters['diag/getCorpId'];
    },
    corpName() {
      return this.$store.getters['diag/getCorpName'];
    },
    corporateFeatureInfoLabels() {
      return this.$store.getters['diag/getCorporateFeatureInfoLabels'];
    },
    diagResultId() {
      return this.$store.getters['diag/getDiagResultId'];
    },
    diagTitle() {
      return this.$store.dispatch('diag/getTitle');
    },
    progress() {
      return Math.floor((this.latestDiagResult.score / this.latestDiagResult.totalScore) * 100);
    },
    score() {
      return this.$store.getters['diag/getScore'];
    },
    totalScore() {
      return this.$store.getters['diag/getTotalScore'];
    },
    hasRiskDiagResultItemsCount() {
      return this.latestDiagResult.hasRiskDiagResultItems.slice(0, 5);
    },
    manageCorpName() {
      return this.$store.getters['auth/getUser'].manageCorpNm;
    },
  },
  // corpIdの監視　法人切替時に実行される
  watch: {
/*    async corpId() {
      this.isRendering = true;
      this.isProcessing = true;
      this.$store.dispatch('diag/setDiagResultId', null);
      await this.getLatestDiagResultForCorporate(this.corpId, this.diagResultId);
      await this.getDiagResultList(this.corpId);
      this.$nextTick(() => {
        this.isRendering = false;
        this.isProcessing = false;
      });
      this.$router.push({name: 'hr-top', params: { corpId: this.corpId }});

      await this.checkParams(this.$route.params);
    },*/
    diagResultId() {
      this.$store.dispatch('diag/initEachSearchForm');
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);

    jQuery(function ($) {


      $('[data-tooltip]').each(function(id) {
        var _text = $(this).attr('data-tooltip');
        var _pos = $(this).attr('data-tooltip-pos');
        var _posclass = '';
        if(_pos == "right") {
          _posclass = 'is-right';
        }
        if(_pos == "bottom") {
          _posclass = 'is-bottom';
        }
        $(this).append('<span class="tooltip ' +_posclass + '">' + _text + '</span>');
      });

      $('[data-tooltip]').on('mouseover', function(){
        event.stopPropagation();  // 他の要素へのイベント伝播を停止
        var _h = $(this).children('.tooltip').height();
        _h += 15;
        if (!$(this).children('.tooltip').hasClass('is-right')) {
            $(this).children('.tooltip').css('top', -(_h));
        }
        if ($(this).children('.tooltip').hasClass('is-bottom')) {
            $(this).children('.tooltip').css('top', 'auto');
            $(this).children('.tooltip').css('bottom', -(_h));
        }
        $(this).children('.tooltip').fadeIn(200);
      });
      $('[data-tooltip]').on('mouseleave', function(){
        $(this).children('.tooltip').fadeOut(200);
      });
      $(document).on('click', function() {
          $('.tooltip').fadeOut(200); // 画面のどこかがクリックされたときにツールチップを隠す
      });
    })

  },
  created: async function () {
    this.isRendering = true;
    this.isProcessing = true;

    const authority = await this.$store.dispatch('auth/getAuthority')
    this.isSystemAdmin = authority.isSystemAdmin;
    this.isAllPermission = authority.isAllPermission;
    this.isAdmin = authority.isAdmin;
    this.isNormal = authority.userAuthNo==9;
    this.isDiagAvailablePlan = authority.isDiagAvailablePlan;

    // パラメータのチェック
    await this.checkParams(this.$route.params);

    this.isRendering = false;
    this.isProcessing = false;
  },
  methods: {

    /**
     * 年度追加処理
     */
    async createNewYear() {
      this.isProcessing = true;
      let res = await this.$axios.post('/ld/api/hr/createNewYear', { corpId: this.corpId, year: this.currentYear });
      if ( res.status == 200 ) {
          this.$toast.info('登録が完了しました');
        // 人的資本データの取得
        let res = await this.$axios.post('/ld/api/hr/getHumanCapitalListData', { corpId: this.corpId });
        if (res.status === 200) {
          this.achievements = res.data.inputForm.achievements;
          this.averages = res.data.inputForm.averages;
          this.accountMonth = res.data.inputForm.accountMonth;
          this.currentYear = res.data.inputForm.currentYear;
          this.dispYear = res.data.inputForm.dispYear;
          this.hasCurrentYearAchievementData = res.data.inputForm.hasCurrentYearAchievementData;
          this.showCreate = false;
        } else {
          this.$toast.error(res.data.message);
        }
      } else {
        this.$toast.error(res.data.message);
      }
      // モーダル画面を閉じる
      this.closeAddYearAlert();
      this.isProcessing = false;
    },

    closeAddYearAlert() {
      this.addYearAlert = false;
    },
    addYear() {
      this.addYearAlert = true;
    },

    goCorp() {
      this.$router.push({ name: 'corporate-edit', query: { corpId: this.corpId } });
    },

    // 遷移関数
    goPages(num, type) {
      this.$router.replace({path: '/hr/'+this.corpId + '/' + this.transitionList[num] + '_' + type});
    },
    // 遷移関数
    goSummary() {
      this.$router.replace({path: '/hr/'+this.corpId + '/summary_graph'});
    },



    /**
     * パラメータチェック
     */
    async checkParams(params) {
      let res = await this.$axios.post('/ld/api/hr/checkParams', { corpId: params.corpId, });
      if (res.status === 500) {
        // checkParamsでエラーになったときは、正常なcorpIdが引き継がれていなかったということで、人的資本管理TOP画面に遷移する
        let corpListRet = await this.$axios.post('/ld/api/corporate/getClientCorporateList', {});
        if (corpListRet) {
          if ( this.corpId == null ){
            this.$store.dispatch('diag/setCorpId', corpListRet.data.corporateList[0].corpId);
            this.$store.dispatch('diag/setDiagResultId', null);
            this.$store.dispatch('diag/setDiagResultItemId', null);
          }
          this.$toast.error('企業情報が見つからなかったので、人的資本管理Top画面に戻ります。');
          this.$router.replace({path: '/hr/'+this.corpId+'/topparam'});

          // 以下、デフォルト企業情報を取得する処理
          await this.getCorporateInfo(this.corpId);
          await this.getDiagResultTitle(this.diagResultId);
          // 人的資本データの取得
          await this.getHumanCapitalListData();
          this.isProcessing = false;
        }
      }else{
        if ( this.corpId !== params.corpId ){
          this.$store.dispatch('diag/setCorpId', params.corpId);
          this.$store.dispatch('diag/setDiagResultId', null);
          this.$store.dispatch('diag/setDiagResultItemId', null);
          await this.getCorporateInfo(this.corpId);
        }
//        await this.getDiagResultTitle(this.diagResultId);
        // 人的資本データの取得
        await this.getHumanCapitalListData();
        this.isProcessing = false;
      }
    },
    async getHumanCapitalListData() {
      // 人的資本データの取得
      let res = await this.$axios.post('/ld/api/hr/getHumanCapitalListData', { corpId: this.corpId });
      if (res.status == 200) {
        if ( res.data.status == 0 ) {
          this.achievements = res.data.inputForm.achievements;
          this.averages = res.data.inputForm.averages;
          this.accountMonth = res.data.inputForm.accountMonth;
          this.currentYear = res.data.inputForm.currentYear;
          this.dispYear = res.data.inputForm.dispYear;
          this.hasCurrentYearAchievementData = res.data.inputForm.hasCurrentYearAchievementData;
          this.showSetting = false;
          this.showCreate = false;
        }else if ( res.data.status == -1 ) {
          // 決算月もしくは業種が登録されていない
          this.showCreate = false;
          this.showSetting = true;
          this.corporateFeatureInfo = res.data.corporateFeatureInfo;
          this.IndustoryItemChange();
        }else if ( res.data.status == -2 ) {
          // 決算月もしくは業種が登録されているが、データがない
          this.showSetting = false;
          this.showCreate = true;
          this.currentYear = res.data.currentYear;
        }else {
          // error
        }
      }
    },
    async getCorporateInfo(corpId) {
      if (corpId) {
        let res = await this.$axios.post('/ld/api/hr/getCorporateFeatureInfoLabels', {
          corpId: corpId,
        });
        if (res.status == 200) {
          this.$store.dispatch('diag/setCorpName', res.data.corporateName);
          this.$store.dispatch('diag/setCorporateFeatureInfoLabels',  res.data.corporateFeatureInfoLabels);
        }else{
          this.$toast.error(res.data.message);
          this.isProcessing = false;
        }
      }
    },
    // 特徴データを取得して設定
    async getCorporateFeatureInfoLabels(corpId) {
      let res = await this.$axios.post('/ld/api/corporate/getCorporateFeatureInfoLabels', {
        corpId: corpId,
      });
      if (res) {
        this.$store.dispatch('diag/setCorporateFeatureInfoLabels', res.data.corporateFeatureInfoLabels);
      }
    },
    async getDiagResultTitle(diagResultId) {
      if (diagResultId) {
        let res = await this.$axios.post('/ld/api/diag/getDiagResultTitle', {
          diagResultId: diagResultId,
        });
        if (res.status==200) {
          this.$store.dispatch('diag/setTitle', res.data.diagTitle);
        }else{
          this.$toast.error(res.data.message);
        }
      }
    },
    async getLatestDiagResultForCorporate(corpId, diagResultId = null) {
      if (corpId) {
        let res = await this.$axios.post('/ld/api/diag/getLatestDiagResultForCorporate', {
          corpId: corpId,
          diagResultId: diagResultId,
        });
        if (res.status) {
          this.latestDiagResult = res.data.latestDiagResult;
          this.$store.dispatch('diag/setDiagResultId', this.latestDiagResult.diagResultId);
          this.$store.dispatch('diag/setTitle', this.latestDiagResult.title);
          this.$store.dispatch('diag/setDiagCourseName', this.latestDiagResult.courseName)
          this.$store.dispatch('diag/setCorpName', this.latestDiagResult.corpNm);
          this.$store.dispatch('diag/setScore', this.latestDiagResult.score);
          this.$store.dispatch('diag/setTotalScore', this.latestDiagResult.totalScore);
          this.$store.dispatch('diag/setDiagRevisionFlg', this.latestDiagResult.diagRevisionFlg);
          this.$store.dispatch('diag/setIsOptionDiag', this.latestDiagResult.isOptionDiag);
          this.$store.dispatch('diag/setSectionNotUseFlag', this.latestDiagResult.sectionNotUseFlag);
          this.$store.dispatch('diag/setCorporateFeatureInfoLabels', this.latestDiagResult.corporateFeatureInfoLabels);
          window.scrollTo({
            top: 0,
            behavior: "instant"
          });
        }else{
          this.$toast.error(res.data.message);
        }
      }
    },
    emitSelectCorpList(params) {
      this.$store.dispatch('diag/setCorpId', params.corpId);
      this.$store.dispatch('diag/setCorpName', params.corpNm);

      this.$router.replace({name: 'hr-top', params: { corpId: this.corpId }});
      this.$router.go({path: this.$router.currentRoute.path, force: true})
    },
    showTutorialModal(value) {
      this.isShowTutorial = value;
    },
    // セレクトボックスの値に応じてサブセレクトボックスを更新
    IndustoryItemChange() {
      if (this.corporateFeatureInfo.industoryItemId != null) {
        let itemId =String(this.corporateFeatureInfo.industoryItemId);
        if ( itemId.length > 0 && itemId != '') {
          this.industorySubitemArray = constant.INDUSTORY_SUBITEM[this.corporateFeatureInfo.industoryItemId]['options'];
          this.industorySubitemDisabled = false;
        }else{
          this.industorySubitemArray = [];
          this.corporateFeatureInfo.industorySubitemId = null;
          this.industorySubitemDisabled = true;
        }
      }else{
          this.industorySubitemArray = [];
          this.corporateFeatureInfo.industorySubitemId = null;
          this.industorySubitemDisabled = true;
      }
    },
    async saveCorpSettings() {
      this.$v.$touch();
      if (!this.$v.$invalid){

        this.isProcessing = true;
        let res = await this.$axios.post('/ld/api/hr/saveCorpSettings', {
          corpId: this.corpId,
          industoryItemId: this.corporateFeatureInfo.industoryItemId,
          industorySubitemId: this.corporateFeatureInfo.industorySubitemId,
          accountClosingMonth: this.corporateFeatureInfo.accountClosingMonth
        });
        if (res.status === 200) {
          this.$toast.info('法人情報の登録が完了しました');
          this.currentYear = res.data.currentYear;
          this.showSetting = false;
          this.showCreate = true;
        } else {
          this.$toast.error('エラーが発生しました。');
        }
        this.isProcessing = false;
      }
    }
  }
}
</script>

<style scoped>
.diagtitlechange-fade-enter-active {
  transition: all .2s ease;
}
.diagtitlechange-fade-leave-active {
  transition: all .2s ease;
}
.diagtitlechange-fade-enter
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateY(-50px);
  opacity: 0;
}

.diagtitlechange-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateY(-50px);
  opacity: 0;
}

.tutorialmodal-fade-enter-active {
  transition: all .2s ease;
}
.tutorialmodal-fade-leave-active {
  transition: all .2s ease;
}
.tutorialmodal-fade-enter {
  transform: translateY(-50px);
  opacity: 0;
}
.tutorialmodal-fade-leave-to {
  transform: translateY(-50px);
  opacity: 0;
}
</style>
